import {
  clearSession,
  CLEAR_SESSION_REASON,
} from '@/unified-modules/account/account-library';
import { removeCookieSessionContext } from '@/utilities/setters/cookieSetters';
import { resetQueueState, setUserSessionContext } from '@/store/actions.ts';
import { handleError } from '../errors/handleError';
import { ErrorType } from '../errors/types';
import { useStore } from '@/store/useStore';
import { promptAccountLogin } from '@/unified-modules/account/prompt-account-login';
import { sendAndroidWrapperEvent } from './wrapperHelpers';
import { ANDROID_WRAPPER_EVENTS } from '@/event-manager/types';
import { resetSessionConsumers } from '@/unified-modules/account/useAccountUm';

export const handleWrapperLogout = (product: string) => {
  try {
    window[product]?.logout();
  } catch (error) {
    handleError({
      type: ErrorType.SESSION,
      payload: {
        message: `[GW][SESSION] Error during ${product} logout`,
        reason: error instanceof Error ? error.message : 'Unknown error',
      },
    });
  }
};

let clearingSessionInProgress = false;

export const clearSessionAndRedirectToLogin = async (
  reason: string = CLEAR_SESSION_REASON.OTHER,
  hidePopup?: () => void,
  shouldClearSession?: boolean,
) => {
  if (clearingSessionInProgress) {
    return;
  }

  clearingSessionInProgress = true;
  try {
    const { wrapperType } = useStore.getState().runtime;
    const { isAndroid, isXsell } = wrapperType;

    if (isAndroid) {
      handleWrapperLogout('AndroidWrapper');
    }

    if (isXsell) {
      handleWrapperLogout('AndroidSportsbook');
    }

    resetQueueState();
    hidePopup?.();
    await clearSession(reason);
    removeCookieSessionContext();

    /**
     * Send event to native Android/Xsell Android to close game when login must be done again.
     * Timeout to ensure session is deleted properly.
     */
    if (isAndroid || isXsell) {
      const timeout = shouldClearSession ? 3000 : 500;
      setTimeout(() => {
        sendAndroidWrapperEvent(ANDROID_WRAPPER_EVENTS.GAME_CLOSE, '');
      }, timeout);
      return;
    }

    const hadActiveSessionBeforeClear =
      useStore.getState().userSessionContext.hasActiveSession;

    setUserSessionContext({ hasActiveSession: false });
    /**
     * Use WEB Account UM to get the login screen.
     * Session clearing is awaited above, so the modal can be presented directly.
     * getSessionAsync('Normal') triggers internal timers after login.
     */
    void promptAccountLogin({
      reason,
      phase: 'post_clear',
      hadActiveSessionBeforeClear,
    });

    resetSessionConsumers(true);
  } catch (error) {
    handleError({
      type: ErrorType.SESSION,
      payload: {
        message: `[GW][SESSION] Error during session clearing`,
        reason: error instanceof Error ? error.message : 'Unknown error',
      },
    });
  } finally {
    clearingSessionInProgress = false;
  }
};
